import Component from "@glimmer/component";
import { task } from "ember-concurrency";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { cached, tracked } from "@glimmer/tracking";
import {
  CONNECTION_TYPES,
  getConnectionOptions,
  getConnectionConfig,
  formatSegmentGroup,
  formatNetworkGroup,
} from "glesys-controlpanel/utils/network-utils";

export default class ModalsCreateNetworkAdapter extends Component {
  @service modal;
  @service cost;
  @service intl;

  @tracked connectionType = CONNECTION_TYPES.PRIVATE;
  @tracked selectedBandwidth;
  @tracked selectedSegment;
  @tracked selectedVlanNetwork;
  @tracked selectedAdapterType = "VMXNET 3";
  @tracked segmentsMap = new Map();
  @tracked vlansMap = new Map();

  get connectionTypes() {
    return CONNECTION_TYPES;
  }

  get server() {
    return this.args.params.server;
  }

  get task() {
    return this.args.params.task;
  }

  get project() {
    return this.args.params.project;
  }

  get isSliderDisabled() {
    return this.connectionType === CONNECTION_TYPES.PRIVATE;
  }

  @cached
  get connectionConfig() {
    return getConnectionConfig({
      selectedSegment: this.selectedSegment,
      selectedVlanNetwork: this.selectedVlanNetwork,
      cost: this.cost,
      server: this.server,
      selectedBandwidth: this.selectedBandwidth,
      connectionType: this.connectionType,
    });
  }

  get isSubmitDisabled() {
    return !this.connectionConfig.isValid();
  }

  get networkAdapterCost() {
    return this.connectionConfig.getCost();
  }

  adapterTypeOptions = Object.freeze([{ value: "VMXNET 3" }, { value: "E1000" }]);

  get connectionOptions() {
    const options = getConnectionOptions(this.connectionType);
    return options.map((option) => ({
      ...option,
      label: this.intl.t(option.labelKey),
    }));
  }

  get bandwidthOptions() {
    return this.connectionConfig.getBandwidthOptions();
  }

  loadSegments = task(async () => {
    let privateNetworks = await this.server.project.privateNetworks;
    let segmentsGroupedByPrivateNetworks = await Promise.all(
      privateNetworks.map(async (privateNetwork) => {
        const filteredSegments = (await privateNetwork.segments).filter(
          (segment) => segment.platform === "vmware" && this.server.datacenter === segment.dataCenter.name,
        );
        filteredSegments.forEach((segment) => {
          this.segmentsMap.set(segment.id, segment);
        });
        return formatSegmentGroup(privateNetwork, filteredSegments);
      }),
    );
    const segmentGroups = segmentsGroupedByPrivateNetworks.filter((group) => group.options.length > 0);
    if (!this.selectedSegment && segmentGroups.length > 0) {
      const firstSegmentId = segmentGroups[0].options[0].value;
      this.selectedSegment = this.segmentsMap.get(firstSegmentId);
    }
    return segmentGroups;
  });

  loadNetworks = task(async () => {
    let networks = await this.project.get("networks");
    let serverDatacenter = this.server.get("datacenter");
    const filteredNetworks = networks.filter(
      (network) => network.id.startsWith("vl") && network.dataCenter === serverDatacenter,
    );
    filteredNetworks.forEach((vlan) => {
      this.vlansMap.set(vlan.id, vlan);
    });
    if (!this.selectedVlanNetwork && filteredNetworks.length > 0) {
      const firstVlanId = filteredNetworks[0].id;
      this.selectedVlanNetwork = this.vlansMap.get(firstVlanId);
    }
    return formatNetworkGroup(filteredNetworks);
  });

  submitTask = task(async () => {
    let task = this.task;
    let adapterType = this.selectedAdapterType;
    let bandwidth = this.selectedBandwidth;
    let payload = { adapterType, bandwidth };
    if (this.connectionType === CONNECTION_TYPES.PRIVATE) {
      payload.segment = this.selectedSegment;
      payload.network = null;
    } else if (this.connectionType === CONNECTION_TYPES.VLAN) {
      payload.network = this.selectedVlanNetwork;
      payload.segment = null;
    }
    await task.perform(payload);

    this.modal.close();
  });

  @action
  handleFormChange({ connectionType, adapterType, segment, network }) {
    this.selectedAdapterType = adapterType;
    if (connectionType && connectionType !== this.connectionType) {
      this.connectionType = connectionType;
      this.selectedBandwidth = this.bandwidthOptions[1];
    }
    if (network) {
      const selectedVlanNetwork = this.vlansMap.get(network);
      if (selectedVlanNetwork) {
        this.selectedVlanNetwork = selectedVlanNetwork;
      }
    }
    if (segment) {
      const selectedSegment = this.segmentsMap.get(segment);
      if (selectedSegment) {
        this.selectedSegment = selectedSegment;
      }
    }
  }

  @action
  selectBandwidth(value) {
    this.selectedBandwidth = value;
  }
}
